import { useCallback, useMemo } from "react";
import { useHeadsUpContext } from "@/components/heads-up/context";
import type { HeadsUpSidebarModel } from "@/components/heads-up/context";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { SmartViewKind } from "@/domains/smart/constants";
import { snakeCase } from "lodash-es";

export enum HeadsUpItemSource {
  Standard = "standard",
  FindMore = "find_more",
}

export interface HeadsUpCardEventMeta {
  section_kind: string;
  section_title?: string | null;
  section_card_id: string;
  section_position_index: number;
  section_is_find_more: boolean;
  item_kind: string;
  item_id: string;
  item_title?: string | null;
  item_position_index: number;
  item_source: HeadsUpItemSource;
}

interface HeadsUpAnalyticsContextPayload {
  context_id?: string | null;
  context_note_id?: string | null;
  context_note_title?: string | null;
  smart_view_id?: string | null;
  smart_view_kind: string;
  active_space_account_id?: string | null;
}

interface HeadsUpAnalytics {
  trackHover: (meta: HeadsUpCardEventMeta) => void;
  trackClick: (meta: HeadsUpCardEventMeta) => void;
  trackRefreshClick: () => void;
  trackRegenerateClick: () => void;
  trackFindMoreClick: () => void;
}

function buildBaseContextPayload(model: HeadsUpSidebarModel): HeadsUpAnalyticsContextPayload {
  const {
    context: { contextId, activeNoteId, activeNoteTitle, smartViewId, spaceAccountId },
  } = model;

  return {
    context_id: contextId ?? undefined,
    context_note_id: activeNoteId ?? undefined,
    context_note_title: activeNoteTitle ?? undefined,
    smart_view_id: smartViewId ?? undefined,
    smart_view_kind: snakeCase(SmartViewKind.HeadsUp),
    active_space_account_id: spaceAccountId ?? undefined,
  };
}

export function useHeadsUpAnalytics(): HeadsUpAnalytics {
  const model = useHeadsUpContext();

  const basePayload = useMemo(() => buildBaseContextPayload(model), [model]);

  const trackHover = useCallback(
    (meta: HeadsUpCardEventMeta) => {
      trackEvent(TrackedEvent.HeadsUpItemHover, {
        ...basePayload,
        ...meta,
      });
    },
    [basePayload]
  );

  const trackClick = useCallback(
    (meta: HeadsUpCardEventMeta) => {
      trackEvent(TrackedEvent.HeadsUpItemClick, {
        ...basePayload,
        ...meta,
      });
    },
    [basePayload]
  );

  const trackRefreshClick = useCallback(() => {
    trackEvent(TrackedEvent.HeadsUpRefreshClick, { ...basePayload });
  }, [basePayload]);

  const trackRegenerateClick = useCallback(() => {
    trackEvent(TrackedEvent.HeadsUpRegenerateClick, { ...basePayload });
  }, [basePayload]);

  const trackFindMoreClick = useCallback(() => {
    trackEvent(TrackedEvent.HeadsUpFindMoreClick, { ...basePayload });
  }, [basePayload]);

  return {
    trackHover,
    trackClick,
    trackRefreshClick,
    trackRegenerateClick,
    trackFindMoreClick,
  };
}
